import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { TaskDefinition, TaskRunState, TaskStatus, TaskType } from "../types";
import { calculateSchedule, formatDuration } from "../shared/scheduling";
import { Calendar, X } from "lucide-react";
import { formatFullDate } from "@/shared/date";

interface TimelineViewProps {
  tasks: TaskDefinition[];
  taskStates?: Record<string, TaskRunState>;
  startTime?: number; // Actual run start time
  scheduledStartTime?: number; // Run scheduled start time
  estimatedStartTime?: number; // Plan estimated start time (for editor)
  onUpdateEstimatedStartTime?: (time: number | undefined) => void;
  currentTime?: number;
  progressPercent?: number;
}

const getTaskNumber = (tasks: TaskDefinition[], taskId: string) => {
  let count = 0;
  for (const t of tasks) {
    if (t.type !== TaskType.SECTION) {
      count++;
    }
    if (t.id === taskId) {
      return t.type === TaskType.SECTION ? null : count;
    }
  }
  return null;
};

const TimelineView: React.FC<TimelineViewProps> = ({
  tasks,
  taskStates,
  startTime,
  scheduledStartTime,
  estimatedStartTime,
  onUpdateEstimatedStartTime,
  currentTime = Date.now(),
  progressPercent = 0,
}) => {
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState(200);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      setSidebarWidth(Math.max(120, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // Use effective start time for calculation: Run Start > Scheduled Run Start > Plan Estimated Start
  const effectivePlanStartTime =
    startTime || scheduledStartTime || estimatedStartTime;

  const { schedule, totalDuration } = useMemo(() => {
    // Pass effectivePlanStartTime to calculateSchedule for Fixed End Time logic
    const s = calculateSchedule(tasks, effectivePlanStartTime);
    let max = 0;
    Object.values(s).forEach((v) => (max = Math.max(max, v.end)));
    return { schedule: s, totalDuration: max || 60 }; // Default to 60m if empty to show scale
  }, [tasks, effectivePlanStartTime]);

  const getStatusColor = (taskId: string) => {
    if (!taskStates) return "bg-indigo-500 dark:bg-indigo-400"; // Editor mode default
    const status = taskStates[taskId]?.status;

    switch (status) {
      case TaskStatus.COMPLETED:
        return "bg-green-500 dark:bg-green-400";
      case TaskStatus.FAILED:
        return "bg-red-500 dark:bg-red-400";
      case TaskStatus.RUNNING:
        return "bg-indigo-500 dark:bg-indigo-400 animate-pulse";
      case TaskStatus.WAITING_FOR_USER:
        return "bg-blue-500 dark:bg-blue-400 animate-pulse";
      case TaskStatus.SKIPPED:
        return "bg-slate-400 dark:bg-slate-500";
      default:
        return "bg-slate-300 dark:bg-slate-600";
    }
  };

  const estimatedLinePct =
    startTime && totalDuration > 0
      ? Math.min(
        100,

        Math.max(
          0,

          ((currentTime - startTime) / 1000 / 60 / totalDuration) * 100,
        ),
      )
      : null;

  const actualLinePct = Math.min(100, Math.max(0, progressPercent));

  const formatTick = (pct: number) => {
    const minutes = Math.round(totalDuration * pct);
    if (effectivePlanStartTime) {
      const date = new Date(effectivePlanStartTime + minutes * 60 * 1000);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return `${minutes}m`;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) return;
    const date = new Date(e.target.value);
    if (!isNaN(date.getTime()) && onUpdateEstimatedStartTime) {
      onUpdateEstimatedStartTime(date.getTime());
      setShowStartDatePicker(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="h-full flex flex-col bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden relative"
    >
      {/* Estimated Time Line (Ghost) */}

      {estimatedLinePct !== null && (
        <div
          className={`absolute top-0 bottom-0 z-10 border-l-2 border-slate-400 dark:border-slate-500 border-dashed opacity-50 pointer-events-none ${isResizing ? "" : "transition-all duration-[1000ms] ease-linear"
            }`}
          style={{
            left: `calc(${sidebarWidth}px + (100% - ${sidebarWidth}px) * ${estimatedLinePct / 100})`,
          }}
          title="Estimated Progress"
        >
          <div className="absolute -top-1 -left-[6px] w-2.5 h-2.5 bg-slate-400 dark:border-slate-500 rounded-full" />
        </div>
      )}

      {/* Actual Progress Line */}

      <div
        className={`absolute top-0 bottom-0 z-20 border-l-2 border-indigo-500 dark:border-indigo-400 pointer-events-none ${isResizing ? "" : "transition-all duration-[1000ms] ease-linear"
          }`}
        style={{
          left: `calc(${sidebarWidth}px + (100% - ${sidebarWidth}px) * ${actualLinePct / 100})`,
        }}
        title="Actual Progress"
      >
        <div className="absolute -top-1 -left-[6px] w-2.5 h-2.5 bg-indigo-500 dark:bg-indigo-400 rounded-full shadow-sm" />
      </div>

      {/* Resizer Handle */}
      <div
        onMouseDown={startResizing}
        className={`absolute top-0 bottom-0 w-1 cursor-col-resize hover:bg-indigo-500/30 transition-colors z-40 ${isResizing ? "bg-indigo-500/50" : ""}`}
        style={{ left: sidebarWidth - 2 }}
      />

      {/* Timeline Header Scale */}
      <div className="flex border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-xs text-slate-500 dark:text-slate-400 font-mono relative z-30">
        <div
          style={{ width: sidebarWidth }}
          className="p-2 border-r border-slate-200 dark:border-slate-700 flex-shrink-0 font-bold uppercase flex items-center justify-between"
        >
          <span className="truncate">Task</span>
          {onUpdateEstimatedStartTime && (
            <div className="relative flex-shrink-0">
              {effectivePlanStartTime && !startTime && !scheduledStartTime ? (
                <button
                  onClick={() => onUpdateEstimatedStartTime(undefined)}
                  className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
                  title="Clear estimated start time"
                >
                  <X size={12} />
                </button>
              ) : !startTime && !scheduledStartTime ? (
                <button
                  onClick={() => setShowStartDatePicker(!showStartDatePicker)}
                  className="p-1 text-slate-400 hover:text-indigo-500 rounded transition-colors"
                  title="Set estimated start time"
                >
                  <Calendar size={12} />
                </button>
              ) : null}

              {showStartDatePicker && (
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 p-2 rounded shadow-xl border border-slate-200 dark:border-slate-700 z-50 min-w-[200px]">
                  <label className="block text-[10px] font-bold uppercase mb-1 text-slate-500">
                    Est. Start Time
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full text-xs p-1 border rounded dark:bg-slate-900 dark:border-slate-600 dark:text-white"
                    onChange={handleDateChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 relative h-8">
          {[0, 0.25, 0.5, 0.75, 1].map((pct) => (
            <div
              key={pct}
              className="absolute top-0 bottom-0 border-l border-slate-200 dark:border-slate-700 pl-1 pt-2 whitespace-nowrap"
              style={{ left: `${pct * 100}%` }}
            >
              {formatTick(pct)}
            </div>
          ))}
          {effectivePlanStartTime && (
            <div className="absolute top-0 right-0 p-1 text-[10px] text-slate-400 italic">
              {formatFullDate(effectivePlanStartTime)}
            </div>
          )}
        </div>
      </div>

      {/* Timeline Rows */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-800">
        {tasks.map((task) => {
          const sched = schedule[task.id];
          if (!sched) return null;

          const startPct = (sched.start / totalDuration) * 100;
          const widthPct = Math.max(
            ((sched.end - sched.start) / totalDuration) * 100,
            0.5,
          ); // Min width for visibility

          if (task.type === TaskType.SECTION) {
            return (
              <div
                key={task.id}
                className="flex border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30"
              >
                <div
                  style={{ width: sidebarWidth }}
                  className="p-2 border-r border-slate-200 dark:border-slate-700 flex-shrink-0 font-bold text-xs text-slate-700 dark:text-slate-300 uppercase truncate"
                  title={task.name}
                >
                  {task.name}
                </div>
                <div className="flex-1 relative h-8 bg-slate-100/30 dark:bg-slate-700/10"></div>
              </div>
            );
          }

          const taskNumber = getTaskNumber(tasks, task.id);
          const duration = sched.end - sched.start; // Calculate visual duration

          return (
            <div
              key={task.id}
              className="flex border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group"
            >
              <div
                style={{ width: sidebarWidth }}
                className="p-2 border-r border-slate-200 dark:border-slate-700 flex-shrink-0 text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2 truncate"
                title={task.name}
              >
                <span className="text-slate-400 dark:text-slate-500 font-mono w-4 text-right flex-shrink-0">
                  #{taskNumber}
                </span>
                <span className="truncate">{task.name}</span>
              </div>
              <div className="flex-1 relative h-8 py-1.5">
                {/* Grid Lines */}
                <div className="absolute inset-0 flex pointer-events-none">
                  {[0, 0.25, 0.5, 0.75, 1].map((p) => (
                    <div
                      key={p}
                      className="h-full border-l border-slate-50 dark:border-slate-700 w-full opacity-50 last:border-0"
                      style={{ flex: 1 }}
                    ></div>
                  ))}
                </div>
                <div
                  className={`absolute h-5 rounded-md shadow-sm border border-white/20 transition-all ${getStatusColor(task.id)}`}
                  style={{
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                  }}
                  title={`Start: ${sched.start}m, Duration: ${formatDuration(duration)}`}
                >
                  {widthPct > 5 && (
                    <span className="text-[10px] text-white font-bold px-1.5 flex items-center h-full drop-shadow-md truncate">
                      {duration > 0 ? formatDuration(duration) : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimelineView;
